import { ButtonLink } from "@ui/ButtonLink";
import { ListTitle } from "@ui/ListTitle";
import { MaterialSymbol } from "@ui/MaterialSymbol";
import { Carousel } from "@ui/molecules/Carousel/Carousel";
import { Pill } from "@ui/Pill";
import { HStack, VStack } from "@ui/Stack";
import { Text } from "@ui/typography/Text";
import { pluralise } from "@utils/text";
import { secondsToTime } from "@utils/time";
import { For, Show } from "solid-js";
import { MetaList } from "@ui/MetaList";
import {
	boardIconSymbol,
	formatCount,
	myAskPageUrl,
	viewerIsOwner,
} from "../askPageAccess";
import { askPageVars } from "../pageVars";
import { AskPageEmptyState } from "./AskPageEmptyState";

type Props = {
	onPlayClip: (boardId: string, clipId: string) => void;
};

export const BoardsShelf = (props: Props) => {
	const vars = askPageVars();
	const isOwner = viewerIsOwner(vars.status);

	return (
		<Show
			when={vars.boards.length > 0}
			fallback={
				<Show when={isOwner}>
					<VStack spacing="large">
						<HStack align="center" spacing="medium">
							<ListTitle>Boards</ListTitle>
							<div class="askgvt1-suggestions-rule" aria-hidden="true"></div>
						</HStack>
						<AskPageEmptyState
							symbol="space_dashboard"
							title="Feature your boards here"
							body="Pick public boards to show visitors your best clips, grouped by topic."
							action={{ label: "Add boards", href: myAskPageUrl("page") }}
						/>
					</VStack>
				</Show>
			}
		>
			<VStack spacing="xlarge">
				<HStack align="center" spacing="medium">
					<ListTitle>Boards </ListTitle>
					<Text size="xsmall" color="fg3" family="mono">
						{vars.boards.length} {pluralise(vars.boards.length, "board")}
					</Text>
					<div class="askgvt1-suggestions-rule" aria-hidden="true"></div>
				</HStack>
				<For each={vars.boards}>
					{(board) => {
						const boardUrl = `/@${vars.influencer_handle}/boards/${board.id}`;
						return (
							<VStack spacing="large">
								<HStack align="center" justify="space-between">
									<HStack align="center" spacing="large">
										<MaterialSymbol
											container={{
												size: "2rem",
												borderRadius: "0.5rem",
												background: "rgba(69, 185, 180, 0.1)",
											}}
											color="primary"
											symbol={boardIconSymbol(board.icon)}
											relativeSize={0.9}
										/>
										<Text size="medium-large" weight="semi-bold" color="ink">
											{board.title}
										</Text>
										<MetaList>
											<li>
												{board.clip_count} {pluralise(board.clip_count, "clip")}
											</li>
										</MetaList>
									</HStack>

									<ButtonLink
										href={boardUrl}
										symbol="chevron_right"
										symbolPosition="after"
										variant="text"
										theme="black"
									>
										View board
									</ButtonLink>
								</HStack>
								<Carousel
									class="askgvt1-boards"
									scrollMode="buttons"
									layout="standard"
								>
									<For each={board.clips}>
										{(clip) => (
											<button
												type="button"
												class="askgvt1-boards-clip"
												onClick={() => props.onPlayClip(board.id, clip.id)}
											>
												<div class="askgvt1-boards-thumb">
													<Show when={clip.url}>
														{(url) => <img src={url()} alt="" />}
													</Show>
													<Pill
														class="askgvt1-boards-duration"
														theme="dark"
														size="small"
													>
														{secondsToTime(clip.duration, "MM:SS")}
													</Pill>
												</div>
												<Show when={clip.title}>
													<Text size="xsmall" color="ink" weight="semi-bold">
														{clip.title}
													</Text>
												</Show>
												<Show when={clip.view_count != null}>
													<Text size="xsmall" color="fg3" family="mono">
														{formatCount(clip.view_count)} views
													</Text>
												</Show>
											</button>
										)}
									</For>
								</Carousel>
							</VStack>
						);
					}}
				</For>
			</VStack>
		</Show>
	);
};
